import React, { useState, useEffect } from "react";
// import { useNavigate } from "react-router-dom";
import styles from "./userManagement.module.css";

import UserForm from "./Utils/UserForm";
import AuthorUserForm from "./Utils/AuthorUserForm";

import { useAuth } from "../../Context/AuthContext";
import { checkEmailAvailability } from "../../Api/Backend/Auth";
import { getCreatePayload, getUpdatePayload } from "./Utils/PayloadBuilders";
import { parseBirthDate, parseIdentificationNumber } from "./Utils/DataParsers";
import { createUser, updateUser, fetchUserReferences, deleteUser } from "../../Api/Backend/User";


// Form used by "GUEST", "MANAGER" & "ADMIN"
const defaultGuest = {
  authMethod: "",
  email: "",
  password: "", // Only used in account creation
  name: "",
  gender: "",
  birthYear: "",
  birthMonth: "",
  birthDay: "",
  phoneNumber: "",
  profileImageUrl: null,
};

// Form used by "AUTHOR"
const defaultAuthor = {
  authMethod: "",
  email: "",
  password: "", // Only used in account creation
  name: "",
  gender: "",
  birthYear: "",
  birthMonth: "",
  birthDay: "",
  phoneNumber: "",
  profileImageUrl: null,
  idFirstHalf: "",
  idSecondHalf: "",
  address: "",
  deliveryAddress: "",
  useSameAddress: false,
  bankName: "",
  bankAccountNumber: "",
  penName: "",
  education: "",
  degree: "UNKNOWN",
  major: "",
  organization: "",
  rating: "UNKNOWN",
  genre: "",
  subgenre: "",
  career: [],
  notableWork: [],
  sns: [],
};

const sharedFields = [
  "authMethod",
  "email",
  "password",
  "name",
  "gender",
  "birthYear",
  "birthMonth",
  "birthDay",
  "phoneNumber",
  "profileImageUrl",
];

const defaultAuthorityType = "GUEST";

export default function UserDetailed({ selectedEntry, onSuccessHandle }) {
  const auth = useAuth();
  const { user, isLoggedIn } = auth;
  // const navigate = useNavigate();

  const authorityTypes = ["GUEST", "AUTHOR", "MANAGER", "ADMIN"];

  const [authorityType, setAuthorityType] = useState(defaultAuthorityType);
  const [userData, setUserData] = useState(defaultGuest);
  const [authorUserData, setAuthorUserData] = useState(defaultAuthor);

  const handleUserDataChange = (e) => {
    const { name, value } = e.target;
    setUserData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAuthorUserDataChange = (e) => {
    const { name, value: rawValue, type, checked } = e.target;

    let value = type === "checkbox" ? checked : rawValue;

    if ((name === "idFirstHalf" || name === "idSecondHalf") && typeof value === "string") {
      value = value.replace(/[^0-9]/g, "");
    }

    if (name === "useSameAddress") {
      setAuthorUserData((prev) => ({
        ...prev,
        useSameAddress: checked,
        deliveryAddress: checked ? prev.address : "",
      }));
      return;
    }

    setAuthorUserData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddListItem = (listName, newItem) => {
    setAuthorUserData((prev) => ({
      ...prev,
      [listName]: [...prev[listName], newItem],
    }));
  };

  const handleRemoveListItem = (listName, indexToRemove) => {
    setAuthorUserData((prev) => ({
      ...prev,
      [listName]: prev[listName].filter((_, index) => index !== indexToRemove),
    }));
  };

  const handleListItemChange = (listName, index, field, value) => {
    const newList = [...authorUserData[listName]];
    newList[index][field] = value;

    setAuthorUserData((prev) => ({ ...prev, [listName]: newList }));
  };

  const [imagePreview, setImagePreview] = useState(null);

  const isEditMode = Boolean(selectedEntry);

  const handleAuthorityTypeChange = (newAuthorityType) => {
    if (newAuthorityType === "AUTHOR") {
      setAuthorUserData((prev) => ({
        ...defaultAuthor,
        ...prev,
        ...sharedFields.reduce((acc, field) => {
          acc[field] = userData[field];
          return acc;
        }, {})
      }));
    } 
    else if (authorityType === "AUTHOR") {
      setUserData((prev) => ({
        ...defaultGuest,
        ...prev,
        ...sharedFields.reduce((acc, field) => {
          acc[field] = authorUserData[field];
          return acc;
        }, {})
      }));
    }

    setAuthorityType(newAuthorityType);
  };

  const handleImageChange = (e) => {
    if (e.target.files && e.target.files[0])
      setImagePreview(URL.createObjectURL(e.target.files[0]));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const action = isEditMode ? "수정" : "생성";

    const basePayload = isEditMode
      ? getUpdatePayload(authorityType, authorUserData, userData)
      : getCreatePayload(authorityType, authorUserData, userData);

    const payload = {
      ...basePayload,
      authorityType
    };

    if (!isEditMode) {
      const emailAvailable = await checkEmailAvailability(payload.email);
      if (!emailAvailable) {
        alert("이미 사용 중인 이메일주소입니다.");
        return;
      }
    }

    const userId = selectedEntry?.user?.userId;
    if (isEditMode && !userId) {
      alert("수정하려는 사용자의 ID가 없습니다.");
      return;
    }

    console.log("PAYLOAD: ", payload);
    const apiMethod = isEditMode ? updateUser : createUser;

    await apiMethod({
      auth: auth,
      ...(isEditMode ? { userId } : {}),
      payload: payload,
      onSuccess: (data) => {
        // console.log("Result: ", data);
        onSuccessHandle();
      },
      onError: (error) => {
        console.error(`Error ${action.toLowerCase()} user entry:`, error);
        alert(`사용자 ${action} 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.`);
      }
    });
  };

  const handleDelete = async () => {
    const userId = selectedEntry?.user?.userId;
    if (isEditMode && !userId) {
      alert("수정하려는 사용자의 ID가 없습니다.");
      return;
    }

    await fetchUserReferences({
      auth: auth,
      userId: userId,
      onSuccess: async (data) => {
        const affectedTablesMessage = data.length
          ? `삭제 시 다음 테이블의 데이터도 함께 삭제됩니다:\n${data.join(", ")}`
          : "정말로 이 사용자를 삭제하시겠습니까?"

        const confirmDelete = window.confirm(affectedTablesMessage);
        if (!confirmDelete) return;

        await deleteUser({
          auth: auth,
          userId: userId,
          onSuccess: () => {
            alert("사용자가 성공적으로 삭제되었습니다.");
            onSuccessHandle();
          },
          onError: (error) => {
            console.error("사용자 삭제 실패:", error);
            alert("사용자 삭제에 실패했습니다.");
          }
        })

      },
      onError: (error) => {
        console.error("사용자 참조 정보 조회 실패:", error);
        alert("사용자 참조 정보 조회 중 오류가 발생했습니다.");
      }
    })
  };

  useEffect(() => {
    if (!selectedEntry) {
      setUserData(defaultGuest);
      setAuthorUserData(defaultAuthor);
      setAuthorityType(defaultAuthorityType);
      return;
    }

    const { user, author, snsList, careerList, notableWorkList } = selectedEntry;

    if (user?.authorityType === "AUTHOR" && user) {
      const entryData = {
        ...defaultAuthor,
        ...user,
        ...author,
        ...parseIdentificationNumber(author?.identificationNumber),
        ...parseBirthDate(user?.birthDate),
        sns: snsList ?? [],
        career: careerList ?? [],
        notableWork: notableWorkList ?? []
      };

      // console.log("AUTHOR DATA: ", entryData);
      setAuthorUserData(entryData);
      setAuthorityType("AUTHOR");
    } else if (user) {
      const entryData = {
        ...defaultGuest,
        ...user,
        ...parseBirthDate(user?.birthDate)
      };

      // console.log("USER DATA: ", entryData);
      setUserData(entryData);
      setAuthorityType(user.authorityType);
    } else {
      setUserData(defaultGuest);
      setAuthorUserData(defaultAuthor);
      setAuthorityType(defaultAuthorityType);
    }
  }, [selectedEntry]);


  return (
    <div className={styles.formContainerV2}>
      <form onSubmit={handleSubmit}>
        <div className={styles.formHeaderV2}>
          <div className={styles.roleSelector}>
            <span className={styles.formHeaderLabel}>권한 유형</span>
            <select
              value={authorityType}
              onChange={(e) => handleAuthorityTypeChange(e.target.value)}
              disabled={isEditMode && user?.authorityType !== "ADMIN"}
            >
              {authorityTypes
                .filter((value) => value !== "ADMIN" || user?.authorityType === "ADMIN")
                .map((value, index) => (
                  <option key={index} value={value}>{value}</option>
                ))}
            </select>
          </div>
          <div className={styles.formHeaderButtons}>
            <button type="submit" className={styles.formSaveBtn}>
              Save
            </button>
            {isEditMode && (
              <button
                type="button"
                className={styles.formDeleteBtn}
                onClick={handleDelete}
              />
            )}
          </div>
        </div>
        {authorityType === "AUTHOR"
          ?
          <AuthorUserForm
            authorUserData={authorUserData}
            handleAuthorUserDataChange={handleAuthorUserDataChange}
            handleAddListItem={handleAddListItem}
            handleRemoveListItem={handleRemoveListItem}
            handleListItemChange={handleListItemChange}
            imagePreview={imagePreview}
            handleImageChange={handleImageChange}
            isEditMode={isEditMode}
          />
          :
          <UserForm
            userData={userData}
            handleUserDataChange={handleUserDataChange}
            imagePreview={imagePreview}
            handleImageChange={handleImageChange}
            isEditMode={isEditMode}
          />
        }
      </form>
      <p className={styles.requiredNote}>* 필수 입력 항목</p>
    </div>
  );
}
